"use client";

import { useState, useEffect } from "react";
import Image from "next/image";
import { partnerLogo } from "@/utils/partnerLogo";

function LogoCard({ initialLogo, initialName }) {
  const [currentLogo, setCurrentLogo] = useState({
    img: initialLogo,
    altText: initialName,
  });
  const [nextLogo, setNextLogo] = useState({
    img: initialLogo,
    altText: initialName,
  });
  const [isTransitioning, setIsTransitioning] = useState(false);

  useEffect(() => {
    // 15-18 seconds interval for each card to display logo
    const randomInterval = 15000 + Math.random() * 3000;

    // Add initial random delay so cards don't all change at once (spread over 10 seconds)
    const initialDelay = Math.random() * 10000;

    let intervalId;

    const startChanging = setTimeout(() => {
      intervalId = setInterval(() => {
        // Pick a random new logo from partnerLogo
        const randomIndex = Math.floor(Math.random() * partnerLogo.length);
        const picked = partnerLogo[randomIndex];

        setNextLogo(picked);
        setIsTransitioning(true);

        // After transition completes, update current logo
        setTimeout(() => {
          setCurrentLogo(picked);
          setIsTransitioning(false);
        }, 1200);
      }, randomInterval);
    }, initialDelay);

    return () => {
      clearTimeout(startChanging);
      if (intervalId) clearInterval(intervalId);
    };
  }, []);

  return (
    <div
      className="bg-white border-[#e2ecf4] border-solid flex items-center justify-center flex-1 relative overflow-hidden"
      style={{
        height: "115px",
        borderWidth: "0.843px",
        minWidth: 0,
      }}
    >
      {/* Current Logo - fades out */}
      <div
        className="absolute inset-0 flex items-center justify-center px-6 py-4 transition-opacity duration-1000"
        style={{ opacity: isTransitioning ? 0 : 1 }}
      >
        <Image
          src={currentLogo.img}
          alt={currentLogo.altText}
          width={220}
          height={120}
          className="object-contain"
          style={{
            mixBlendMode: "luminosity",
            maxWidth: "100%",
            maxHeight: "100%",
            width: "auto",
            height: "auto",
          }}
        />
      </div>

      {/* Next Logo - fades in */}
      <div
        className="absolute inset-0 flex items-center justify-center px-6 py-4 transition-opacity duration-1000"
        style={{ opacity: isTransitioning ? 1 : 0 }}
      >
        <Image
          src={nextLogo.img}
          alt={nextLogo.altText}
          width={220}
          height={120}
          className="object-contain"
          style={{
            mixBlendMode: "luminosity",
            maxWidth: "100%",
            maxHeight: "100%",
            width: "auto",
            height: "auto",
          }}
        />
      </div>
    </div>
  );
}

export function IndustryCollaborations() {
  // Generate 30 initial logos (cycling through partnerLogo)
  const initialCards = Array.from({ length: 30 }, (_, i) => ({
    cardIndex: i,
    ...partnerLogo[i % partnerLogo.length],
  }));

  return (
    <section className="w-full bg-[#081e2d] py-20">
      <div className="w-full">
        {/* Header */}
        <div className="text-center mb-16 px-6">
          <h2
            className="mb-4 text-white"
            style={{
              fontFamily: "'Playfair Display', serif",
              fontSize: "clamp(2rem, 5vw, 3rem)",
              fontWeight: 700,
            }}
          >
            Our Industry Collaborations
          </h2>
          <p
            className="max-w-3xl mx-auto text-gray-300"
            style={{
              fontFamily: "'Source Sans Pro', sans-serif",
              fontSize: "clamp(1rem, 2vw, 1.125rem)",
              fontWeight: 400,
              lineHeight: 1.6,
            }}
          >
            Trusted by leading organizations across industries
          </p>
        </div>

        {/* Logo Grid */}
        <div className="flex flex-col gap-px overflow-hidden">
          {/* Row 1 */}
          <div className="flex gap-px">
            {initialCards.slice(0, 6).map((card) => (
              <LogoCard
                key={card.cardIndex}
                initialLogo={card.img}
                initialName={card.altText}
                cardIndex={card.cardIndex}
              />
            ))}
          </div>

          {/* Row 2 */}
          <div className="flex gap-px">
            {initialCards.slice(6, 12).map((card) => (
              <LogoCard
                key={card.cardIndex}
                initialLogo={card.img}
                initialName={card.altText}
                cardIndex={card.cardIndex}
              />
            ))}
          </div>

          {/* Row 3 */}
          <div className="flex gap-px">
            {initialCards.slice(12, 18).map((card) => (
              <LogoCard
                key={card.cardIndex}
                initialLogo={card.img}
                initialName={card.altText}
                cardIndex={card.cardIndex}
              />
            ))}
          </div>

          {/* Row 4 */}
          <div className="flex gap-px">
            {initialCards.slice(18, 24).map((card) => (
              <LogoCard
                key={card.cardIndex}
                initialLogo={card.img}
                initialName={card.altText}
                cardIndex={card.cardIndex}
              />
            ))}
          </div>

          {/* Row 5 */}
          <div className="flex gap-px">
            {initialCards.slice(24, 30).map((card) => (
              <LogoCard
                key={card.cardIndex}
                initialLogo={card.img}
                initialName={card.altText}
                cardIndex={card.cardIndex}
              />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
